// react
import * as BusinessLogic from "@/lib/businessLogic";
import * as React from "react";
import type { History, Location } from "history";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import VersionCheck from "./VersionCheck";
import { getHelpDeskHref, SUPPORT_EMAIL } from "./helpDesk";
import { Dropdown, Navbar, Nav } from "react-bootstrap";

// backend
import * as Auth from "@/lib/auth";
import { PLANT_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import { USER_TAGS } from "@/lib/CommonTags.generated";
import { getAnonymization } from "@/app/anonymization";
import { translate } from "@/modules/shared/IntlProviderWrapper";

import { MANUFACTURING_PREFIX, MANUFACTURING_SKIP_BACK, ADMIN_PREFIX, DIGITAL_TWIN_PREFIX } from "@/app/App";
import TicketingHeaderIcon from "@/modules/ticketing/TicketingHeaderIcon";
import UserDocumentationLink from "@/modules/shared/UserDocumentationLink";

import AppsIcon from "@/modules/shared/ui/Icon/Apps";
import ArrowCircleLeftIcon from "@/modules/shared/ui/Icon/ArrowCircleLeft";
import ChevronDownIcon from "@/modules/shared/ui/Icon/ChevronDown";
import CogIcon from "@/modules/shared/ui/Icon/Cog";
import LeapLogoIcon from "@/modules/shared/ui/Icon/LeapLogo";
import PowerOffIcon from "@/modules/shared/ui/Icon/PowerOff";
import ProductionGuidingIcon from "@/modules/shared/ui/Icon/ProductionGuiding";
import UserCircleIcon from "@/modules/shared/ui/Icon/UserCircle";
import UserIcon from "@/modules/shared/ui/Icon/User";

import type { IPlantData, ILineGroupData } from "@/lib/backend/manufacturing2.generated.types";
import type { SvgIconProps } from "@/modules/shared/ui/SvgIcon";
import * as Utils from "@/lib/util";

import "./Header.scss";

import img_warning from "@/img/warning.svg";

type Props = {
    history: History;
    location: Location;
    fixed: boolean;
    show: boolean;
    username: string;
    env_flag: string;
    show_manufacturing: boolean;
    // manufacturing
    plants: IPlantData[];
    plant_uuid: string;
    set_plant_uuid: (uuid: string) => void;
    line_groups: ILineGroupData[];
    line_group_uuid: string;
    set_line_group_uuid: (uuid: string) => void;
};

type State = {
    show_version_notification: boolean;
    error: string;
    user_title: string;
};

type LeapModule = {
    id: string;
    title: string;
    home_path: string;
    path: string;
    icon: React.ComponentType<SvgIconProps>;
    enabled: boolean;
};

const env_flag = BusinessLogic.getSysFlag("env");

export const getLeapModules = (): LeapModule[] => {
    // check if user is in kiosk mode
    const is_kiosk = Auth.getUserTag(USER_TAGS.kiosk) === "true";

    // is ROLE_ADMIN or ROLE_POWER_USER or ROLE_DEMO_USER
    const is_elevated_role_user = !is_kiosk && Auth.hasPermission(Auth.PERMISSION_NAMES.ElevatedPriviliges);
    // can the user access production guiding
    const can_access_pg = !is_kiosk && Auth.hasPermission(Auth.PERMISSION_NAMES.ProductionGuiding);
    // can the user access digital twin
    const can_access_dt = !is_kiosk && Auth.hasPermission(Auth.PERMISSION_NAMES.DigitalTwin);

    const admin: LeapModule[] = [];
    if (is_elevated_role_user) {
        admin.push({
            id: "administration",
            title: "System Administration",
            path: ADMIN_PREFIX,
            home_path: ADMIN_PREFIX,
            icon: () => <span style={{ fontSize: "16px", marginRight: "18px" }}>SA</span>, // TODO
            enabled: true,
        });
    }

    const menu_items: LeapModule[] = [
        {
            id: "pg",
            title: "Production Guiding",
            path: MANUFACTURING_PREFIX,
            home_path: MANUFACTURING_PREFIX,
            icon: ProductionGuidingIcon,
            enabled: true,
        },
        {
            id: "digital_twin",
            title: "Digital Twin",
            path: DIGITAL_TWIN_PREFIX,
            home_path: DIGITAL_TWIN_PREFIX + "/resources/plants",
            icon: () => <span style={{ fontSize: "16px", marginRight: "18px" }}>DT</span>,
            enabled: true,
        },
    ];

    return menu_items
        .filter(item => {
            // Remove DT from menu if the user can't access it
            if (item.id === "digital_twin" && !can_access_dt) {
                return false;
            }
            // Remove PG from menu if the user can't access it
            if (item.id === "pg" && !can_access_pg) {
                return false;
            }
            return true;
        })
        .concat(admin);
};

export const getActiveLeapModule = (location: Location): LeapModule | void => {
    const leap_modules = getLeapModules();
    const current_location = location.pathname.toLowerCase();
    if (current_location) {
        return leap_modules.find(lm => current_location.startsWith(lm.path));
    }
};

/**
 * Component that displays the header with appropriate menu.
 */
class Header extends React.Component<Props, State> {
    state: State = {
        show_version_notification: false,
        error: "",
        user_title: "",
    };

    componentDidMount() {
        // get user info
        const user_data = Auth.getLoggedinUser();
        this.setState({
            user_title: user_data.title,
            show_version_notification: Auth.shouldNotifyNewVersion(),
        });
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.username !== this.props.username) {
            const user_data = Auth.getLoggedinUser();
            this.setState({ user_title: user_data.title });
        }
    }

    handleLogout = () => {
        Auth.logout();
        this.props.history.push("/");
    };

    // return correct style class for navbar top-level button based on the path
    getHighlightClass(active: boolean) {
        return active ? " active" : "";
    }

    renderManufacturingPlant() {
        // get selected plant title
        const selected_plant = this.props.plants.find(plant => plant.uuid === this.props.plant_uuid);
        const plant_title = selected_plant === undefined ? "" : selected_plant.title;
        // render menu
        return (
            <Nav.Item key="plant_selector">
                <Dropdown>
                    <Dropdown.Toggle as="button" className="btn btn-link nav-link" id="dropdownMenuManufacturingPlant">
                        <span className="text-truncate">{plant_title}</span>
                        <ChevronDownIcon size="xs" class_name="flex-shrink-0" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ maxHeight: "calc(100vh - 50px)", overflowY: "auto" }}>
                        {this.props.plants.map(plant => (
                            <Dropdown.Item
                                as="button"
                                data-test-id={`plant_${plant.title.replace(" ", "-").toLowerCase()}`}
                                key={`plant_${plant.uuid}`}
                                active={plant.uuid === this.props.plant_uuid}
                                onClick={() => this.props.set_plant_uuid(plant.uuid)}
                            >
                                {plant.title}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </Nav.Item>
        );
    }

    renderManufacturingLineGroup() {
        // get selected line group title
        const selected_line_group = this.props.line_groups.find(
            line_group => line_group.uuid === this.props.line_group_uuid
        );
        let line_group_title = selected_line_group === undefined ? "" : selected_line_group.title;
        if (line_group_title === "") {
            line_group_title = "--";
        }
        // render menu
        return (
            <Nav.Item key="line_group_selector">
                <Dropdown>
                    <Dropdown.Toggle
                        as="button"
                        className="btn btn-link nav-link d-flex align-items-center"
                        id="dropdownMenuManufacturingLineGroup"
                    >
                        <span className="text-truncate">{line_group_title}</span>
                        <ChevronDownIcon size="xs" class_name="flex-shrink-0" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ maxHeight: "calc(100vh - 50px)", overflowY: "auto" }}>
                        {this.props.line_groups.map(line_group => (
                            <Dropdown.Item
                                as="button"
                                data-test-id={`line_group_${line_group.title.toLowerCase()}`}
                                // TODO: remove test-id attribute
                                test-id={`line_group_${line_group.title.toLowerCase()}`}
                                key={`line_group_${line_group.uuid}`}
                                active={line_group.uuid === this.props.line_group_uuid}
                                onClick={() => this.props.set_line_group_uuid(line_group.uuid)}
                            >
                                {line_group.title}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </Nav.Item>
        );
    }

    renderManufacturing(path: string) {
        // only show plant/line group selectors when showing manufacturing views
        const show_manufacturing_selectors =
            path.startsWith(MANUFACTURING_PREFIX + "/") && !MANUFACTURING_SKIP_BACK.includes(path);

        if (show_manufacturing_selectors) {
            return [this.renderManufacturingPlant(), this.renderManufacturingLineGroup()];
        }

        const can_access_pg = Auth.hasPermission(Auth.PERMISSION_NAMES.ProductionGuiding);
        if (can_access_pg) {
            return (
                <Nav.Item>
                    <Nav.Link as={Link} to={MANUFACTURING_PREFIX} className="btn btn-link nav-link">
                        <ArrowCircleLeftIcon class_name="mr-2" />
                        <FormattedMessage id="Header.menu.production_guiding" defaultMessage="Production guiding" />
                    </Nav.Link>
                </Nav.Item>
            );
        }

        return null;
    }

    closeDropdown = () => {
        const el = document.querySelector("#header-menu");
        if (el) {
            el.classList.remove("show");
            [...el.children].forEach(ch => {
                if (ch.hasAttribute("aria-expanded")) {
                    ch.setAttribute("aria-expanded", "false");
                } else {
                    ch.classList.remove("show");
                }
            });
        }
    };

    getClassName = (): string => {
        const classes = ["app-header"];
        if (this.props.fixed) {
            classes.push("header_always_top");
        }

        return classes.join(" ");
    };

    getContainerClassName = (): string => {
        const classes = ["app-header-container"];
        const active_leap_module = getActiveLeapModule(this.props.location);
        if (active_leap_module !== undefined) {
            classes.push(`module-leap-${active_leap_module.id}`);
        }

        if (!getAnonymization()) {
            // only apply color for header when anonymization is not active
            const env_flag = this.props.env_flag;
            if (env_flag.includes("dev")) {
                classes.push("env-dev");
            } else if (env_flag.includes("beta")) {
                classes.push("env-beta");
            } else if (env_flag.includes("alpha")) {
                classes.push("env-alpha");
            }
        }
        if (Utils.getGridLayoutSettings()) {
            classes.push("grid-header");
        }
        return classes.join(" ");
    };

    renderLeapModuleNav = () => {
        const leap_modules = getLeapModules();
        const active_module = getActiveLeapModule(this.props.location);

        // if its kiosk_user, we don't want to show the leap modules (9 dots)
        if (Auth.getUserTag(USER_TAGS.kiosk) === "true") {
            return <div style={{ paddingLeft: "20px" }}></div>;
        }

        return (
            <div className="leap-module-nav mr-3">
                <Dropdown>
                    <Dropdown.Toggle
                        as="button"
                        className="btn d-flex align-items-center justify-content-center"
                        type="button"
                        id="leap-module-nav-button"
                    >
                        <AppsIcon size={18} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {leap_modules.map(lm => {
                            if (!lm.enabled) {
                                return null;
                            }

                            const Icon = lm.icon;
                            const is_active = active_module !== undefined && lm.path === active_module.path;
                            return (
                                <Dropdown.Item as={Link} key={lm.id} active={is_active} to={lm.home_path}>
                                    <Icon class_name="vertical-align-middle mr-3" height={20} />
                                    <span className="vertical-align-middle">{lm.title}</span>
                                </Dropdown.Item>
                            );
                        })}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    };

    renderBrand = () => {
        const active_module = getActiveLeapModule(this.props.location);

        const plant_uuid = this.props.plant_uuid;
        const plant_tags = BusinessLogic.getPlantTags(plant_uuid);
        const enable_home = PLANT_TAGS_ACCESS.new_feature_home_button(plant_tags);
        const redirect_url = enable_home ? "/manufacturing/home/overview" : "/manufacturing/lines/gantt";

        return (
            <div className="leap-brand d-flex align-items-center mr-auto" data-testid="logo">
                <Link to={redirect_url} className="navbar-brand">
                    <LeapLogoIcon class_name="d-block" />
                </Link>
                {active_module !== undefined && (
                    <span className="leap-module-icon ml-3" title={active_module.title}>
                        <active_module.icon height={26} />
                    </span>
                )}
            </div>
        );
    };

    /**
     * Renders the component.
     */
    render() {
        if (!this.props.show) {
            return null;
        }

        // get current path so we can highlight correct header button
        const path = this.props.location.pathname;
        // check if user is in kiosk mode
        const is_kiosk = Auth.getUserTag(USER_TAGS.kiosk) === "true";
        // showing manufacturing modules
        const show_manufacturing =
            !is_kiosk && this.props.show_manufacturing && Auth.hasPermission(Auth.PERMISSION_NAMES.ProductionGuiding);
        // is the user in ticket-user role
        const is_ticketing = !is_kiosk && Auth.isInRoles([Auth.ROLE_TICKET_USER, Auth.ROLE_TICKET_ADMIN]);

        // should we fix the header to the top or keep it floating
        const is_fixed_header = this.props.fixed;

        const org = Auth.getOrgData();
        const is_kolektor = org.code === "kolektor";
        const is_power_user = Auth.isInRole(Auth.ROLE_POWER_USER) || Auth.isInRole(Auth.ROLE_ADMIN);
        const plant = this.props.plants.find(plant => plant.uuid === this.props.plant_uuid);
        const app_version = BusinessLogic.getAppVersionShort();
        const loggedInUser = Auth.getLoggedinUser();
        const helpDeskHref = getHelpDeskHref(is_kolektor, {
            username: loggedInUser.username,
            userTitle: loggedInUser.title,
            orgCode: org.code,
            appVersion: app_version,
            pageUrl: window.location.href,
        });
        const contact_item = plant && plant.tags && plant.tags.contact_person && (
            <>
                <div className="dropdown-divider" />
                <a
                    className="dropdown-item"
                    href={plant.tags.contact_person_email && `mailto:${plant.tags.contact_person_email}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    <FormattedMessage id="Header.menu.contact" defaultMessage="Contact" />: {plant.tags.contact_person}
                </a>
            </>
        );

        return (
            <div className={this.getContainerClassName()}>
                <header className={this.getClassName()}>
                    <div className="container-fluid h-100 px-0">
                        <Navbar expand="md" className="h-100">
                            {this.renderLeapModuleNav()}
                            {this.renderBrand()}
                            <Navbar.Toggle aria-controls="navbarSupportedContent">
                                <span />
                                <span />
                                <span />
                            </Navbar.Toggle>
                            <Navbar.Collapse id="navbarSupportedContent">
                                <Nav className="navbar-nav--primary">
                                    {show_manufacturing && this.renderManufacturing(path)}
                                </Nav>
                                <Nav className="navbar-nav--secondary">
                                    <Nav.Item>
                                        <Dropdown>
                                            <Dropdown.Toggle
                                                as="button"
                                                className="btn btn-link nav-link"
                                                id="dropdownMenuHelp"
                                            >
                                                <FormattedMessage id="common.help" defaultMessage="Help" />
                                                <ChevronDownIcon size="xs" />
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item as="a" href={`mailto:${SUPPORT_EMAIL}`}>
                                                    <FormattedMessage
                                                        id="Header.menu.send_message"
                                                        defaultMessage="Send message"
                                                    />
                                                </Dropdown.Item>
                                                <Dropdown.Item as={UserDocumentationLink} link_key={"latest_changes"}>
                                                    <FormattedMessage
                                                        id="Header.menu.documentation_latest_changes"
                                                        defaultMessage="Latest changes"
                                                    />
                                                </Dropdown.Item>
                                                <Dropdown.Item as={UserDocumentationLink} link_key={"home"}>
                                                    <FormattedMessage
                                                        id="Header.menu.user_documentation"
                                                        defaultMessage="User documentation"
                                                    />
                                                </Dropdown.Item>
                                                {((is_kolektor && is_power_user) || !is_kolektor) && (
                                                    <Dropdown.Item
                                                        as="a"
                                                        href={helpDeskHref}
                                                        target={is_kolektor ? "_blank" : undefined}
                                                        rel={is_kolektor ? "noopener noreferrer" : undefined}
                                                    >
                                                        <FormattedMessage
                                                            id="Header.menu.help_desk"
                                                            defaultMessage="Help desk"
                                                        />
                                                    </Dropdown.Item>
                                                )}
                                                {contact_item && <Dropdown.Item as="div">{contact_item}</Dropdown.Item>}
                                                {app_version && (
                                                    <>
                                                        <Dropdown.Divider />
                                                        <Dropdown.ItemText className="dropdown-item dropdown-item--noninteractive">
                                                            <FormattedMessage
                                                                id="common.version"
                                                                defaultMessage="Version"
                                                            />
                                                            : {app_version}
                                                        </Dropdown.ItemText>
                                                    </>
                                                )}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </Nav.Item>
                                    {is_ticketing && (
                                        <Nav.Item>
                                            <TicketingHeaderIcon />
                                        </Nav.Item>
                                    )}
                                    <Nav.Item className="user-account-menu">
                                        <Dropdown>
                                            <Dropdown.Toggle
                                                as="button"
                                                className="btn btn-link nav-link"
                                                id="dropdownMenuUserAccount"
                                            >
                                                <UserIcon size={18} />
                                                <span>{translate("common.user_account", "User account")}</span>
                                                <ChevronDownIcon size="xs" />
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu align="right">
                                                <Dropdown.Item as={Link} to="/settings">
                                                    <UserCircleIcon />
                                                    {this.state.user_title}
                                                </Dropdown.Item>
                                                <Dropdown.Item as={Link} to="/settings">
                                                    <CogIcon />
                                                    <FormattedMessage
                                                        id="Header.menu.settings"
                                                        defaultMessage="Settings"
                                                    />
                                                </Dropdown.Item>
                                                <Dropdown.Divider />
                                                <Dropdown.Item as="button" onClick={this.handleLogout}>
                                                    <PowerOffIcon />
                                                    <FormattedMessage
                                                        id="Header.menu.logout"
                                                        defaultMessage="Log out"
                                                    />
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </Nav.Item>
                                </Nav>
                            </Navbar.Collapse>
                            <div className="menu-org-logo">
                                <img
                                    src={`/dynamic/logo/${org.code}?anon=${getAnonymization() ? "true" : "false"}`}
                                    height="40"
                                    alt="customer_logo"
                                />
                            </div>
                            {env_flag === "dev" && window.location.href.length > 2000 && (
                                <div className="nav-item">
                                    <img
                                        width="30"
                                        height="40"
                                        src={img_warning}
                                        alt="URL is too long"
                                        title="URL is too long"
                                    />
                                </div>
                            )}
                        </Navbar>
                    </div>
                </header>
                {is_fixed_header && <div className="fixed_header_filler"></div>}
                <VersionCheck />
            </div>
        );
    }
}

export default Header;
